import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';

const Quality = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [currentCertIndex, setCurrentCertIndex] = useState(0);

  const certifications = [
    { name: 'IATF 16949:2016', description: 'Automotive Quality Management System Standard — design, development, production and service of automotive products.', year: '2023' },
    { name: 'ISO 14001:2015', description: 'Environmental Management System Standard — effective environmental management and sustainable operations.', year: '2023' },
    { name: 'ISO 45001:2018', description: 'Occupational Health and Safety Management System Standard — ensuring a safe and healthy workplace.', year: '2023' },
  ];

  const certificationImages = ['/16969.jpg', '/14001.jpg', '/45001.jpg'];

  const qualityPoints = [
    t('quality.point1'), t('quality.point2'), t('quality.point3'),
    t('quality.point4'), t('quality.point5'), t('quality.point6'),
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCertIndex((prev) => (prev === certifications.length - 1 ? 0 : prev + 1));
    }, 5200);
    return () => clearInterval(interval);
  }, [certifications.length]);

  return (
    <section id="quality" ref={ref} className="section-pad bg-background">
      <div className="container-tight">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <span className="eyebrow mb-6 block">{t('quality.badge')}</span>
            <h2 className="font-display font-bold text-primary" style={{ fontSize: 'clamp(2.25rem, 4vw, 3.5rem)', lineHeight: 1.02 }}>
              {t('quality.title')}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6 lg:col-start-7 lede pt-2"
          >
            {t('quality.description')}
          </motion.p>
        </div>

        {/* Certifications: list + viewer */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-24">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <h3 className="font-display text-2xl font-semibold text-primary mb-8">
              {t('quality.certifications.title')}
            </h3>
            <div className="space-y-px bg-[hsl(var(--hairline))] border border-[hsl(var(--hairline))] rounded-xl overflow-hidden">
              {certifications.map((cert, i) => {
                const active = currentCertIndex === i;
                return (
                  <button
                    key={i}
                    onClick={() => setCurrentCertIndex(i)}
                    className={`w-full text-left flex items-center justify-between px-7 py-6 transition-colors duration-300 ${
                      active ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted/60'
                    }`}
                  >
                    <div className="flex items-center gap-6">
                      <span className={`font-display text-xs tabular-nums tracking-[0.2em] ${active ? 'text-secondary' : 'text-muted-foreground'}`}>
                        0{i + 1}
                      </span>
                      <div>
                        <div className={`font-display font-semibold ${active ? 'text-white' : 'text-primary'}`}>
                          {cert.name}
                        </div>
                        <div className={`text-xs mt-1 ${active ? 'text-white/70' : 'text-muted-foreground'}`}>
                          Certified · {cert.year}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        active ? 'bg-secondary scale-125' : 'bg-[hsl(var(--hairline-strong))]/30'
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentCertIndex}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
                className="rounded-xl border border-[hsl(var(--hairline))] bg-muted/30 p-10 md:p-14"
              >
                <div className="flex items-baseline justify-between mb-8">
                  <div>
                    <span className="section-num">— {t('sub.standard')}</span>
                    <h4 className="font-display text-3xl md:text-4xl font-bold text-primary mt-2 tracking-tight">
                      {certifications[currentCertIndex].name}
                    </h4>
                  </div>
                  <span className="font-display text-sm tabular-nums text-muted-foreground">
                    /{String(currentCertIndex + 1).padStart(2, '0')}
                  </span>
                </div>
                <div className="bg-background rounded-md border border-[hsl(var(--hairline))] p-8 mb-8 flex items-center justify-center h-64">
                  <img
                    src={certificationImages[currentCertIndex]}
                    alt={certifications[currentCertIndex].name}
                    className="h-full w-auto object-contain"
                  />
                </div>
                <p className="text-foreground/75 leading-relaxed">
                  {certifications[currentCertIndex].description}
                </p>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Commitment */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="surface-dark grain relative rounded-xl overflow-hidden p-10 md:p-16"
        >
          <div
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage: 'linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)',
              backgroundSize: '64px 64px',
            }}
            aria-hidden
          />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-10">
              <span className="eyebrow text-secondary">— {t('sub.ourCommitment')}</span>
            </div>
            <h3 className="font-display font-bold text-white mb-12 max-w-3xl" style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)', lineHeight: 1.05 }}>
              {t('quality.commitment.title')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
              {qualityPoints.map((point, i) => (
                <div
                  key={i}
                  className="bg-primary p-6 md:p-8 flex items-start gap-4 group hover:bg-primary-deep transition-colors"
                >
                  <span className="font-display text-xs text-secondary tabular-nums tracking-[0.2em] pt-1">
                    0{i + 1}
                  </span>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-white/85 text-sm leading-relaxed">{point}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Quality;
